import React, { useEffect, useState } from "react";
import { formatTime } from "./gameUtils";

const F = { fontFamily: "'Fredoka', sans-serif" };

// The score rolls up from zero when the card opens — a little celebration in
// itself.
const useCountUp = (target, active, duration = 900) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) {
      setValue(0);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return value;
};

const ResultIcon = ({ reason }) => {
  if (reason === "bomb") {
    return (
      <svg viewBox="0 0 64 64" className="h-12 w-12" aria-hidden>
        <path
          d="M32 4l7.2 12.2 13.7-3.5-3.5 13.7L61.6 32l-12.2 5.6 3.5 13.7-13.7-3.5L32 60l-7.2-12.2-13.7 3.5 3.5-13.7L2.4 32l12.2-5.6-3.5-13.7 13.7 3.5L32 4z"
          fill="currentColor"
        />
        <path d="M23 23l18 18M41 23L23 41" stroke="#1594EE" strokeWidth="5" strokeLinecap="round" />
      </svg>
    );
  }
  if (reason === "wrongColor") {
    return (
      <svg viewBox="0 0 64 64" className="h-12 w-12" aria-hidden>
        <path
          d="M32 6C17 6 5 17 5 31c0 14 12 25 27 25 3 0 5-2 5-5 0-1.4-.6-2.6-1.5-3.5-.9-.9-1.5-2.1-1.5-3.5 0-2.8 2.2-5 5-5h6c8 0 14-6 14-13C58.9 15 46.9 6 32 6z"
          fill="currentColor"
        />
        <circle cx="18" cy="28" r="4.5" fill="#FC73B8" />
        <circle cx="28" cy="18" r="4.5" fill="#FFD84D" />
        <circle cx="42" cy="19" r="4.5" fill="#45CC73" />
        <circle cx="50" cy="30" r="4.5" fill="#9E6BF5" />
      </svg>
    );
  }
  if (reason === "math") {
    return (
      <svg viewBox="0 0 64 64" className="h-12 w-12" aria-hidden>
        <circle cx="32" cy="26" r="17" fill="currentColor" />
        <path d="M32 17l2.7 5.6 6.2.8-4.5 4.2 1.1 6.1L32 30.8l-5.5 2.9 1.1-6.1-4.5-4.2 6.2-.8z" fill="#0E8A80" />
        <path d="M22 40l-6 16 10-5 6 5V42M42 40l6 16-10-5-6 5V42" fill="currentColor" opacity="0.85" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 64 64" className="h-12 w-12" aria-hidden>
      <circle cx="32" cy="27" r="20" fill="currentColor" />
      <path d="M28 47h8l-4 6-4-6z" fill="currentColor" />
      <path d="M32 52c5 3 1 7 6 9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <ellipse cx="25" cy="18" rx="6" ry="4" fill="rgba(255,255,255,0.45)" transform="rotate(-28 25 18)" />
    </svg>
  );
};

const HEADER_BUBBLES = [
  { left: "10%", size: 26, delay: 0, duration: 4.2 },
  { left: "24%", size: 14, delay: 1.4, duration: 3.4 },
  { left: "58%", size: 18, delay: 0.7, duration: 3.9 },
  { left: "76%", size: 30, delay: 2.0, duration: 4.6 },
  { left: "90%", size: 12, delay: 0.3, duration: 3.2 },
];

const StatChip = ({ icon, tint, children, index }) => (
  <div
    className="bpm-stat flex items-center gap-3.5 rounded-[18px] bg-[#F4F8FF] px-4 py-3"
    style={{ animationDelay: `${450 + index * 120}ms` }}
  >
    <span
      className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full text-[18px]"
      style={{ ...F, color: "#FFFFFF", fontWeight: 700, background: tint, boxShadow: `0 3px 8px ${tint}66` }}
    >
      {icon}
    </span>
    <span className="text-[17px] leading-snug" style={{ ...F, color: "#22335C", fontWeight: 650 }}>
      {children}
    </span>
  </div>
);

const BubbleGameOverModal = ({
  open,
  reason,
  title: titleOverride,
  score,
  starsAwarded,
  survivedSeconds,
  bestCombo,
  solved = 0,
  favoritePop,
  onRetry,
  onBack,
  accent = "#21B3FF",
  accentDeep = "#007AEF",
  // Beat-my-score (all optional — modals without them render as before):
  challengeTarget = null, // score the player was challenged to beat
  isGuest = false, // signed-out visitor from a challenge link
  onSignup, // guest CTA action
  onChallengeShare, // share YOUR score as a new challenge
  challengeCopied = false, // clipboard-fallback feedback
}) => {
  const displayScore = useCountUp(score, open);
  if (!open) return null;

  const title =
    titleOverride ||
    (reason === "math" ? "Great math practice!" : reason === "bomb" ? "Boom!" : "They floated away!");

  const stats = [];
  if (solved > 0) stats.push({ icon: "✓", tint: "#2FB56E", text: `You solved ${solved} problem${solved === 1 ? "" : "s"}` });
  if (survivedSeconds > 0) stats.push({ icon: "◷", tint: accentDeep, text: `You played for ${formatTime(survivedSeconds)}` });
  if (bestCombo >= 2) stats.push({ icon: "×", tint: "#F5A21B", text: `Best streak ×${bestCombo}` });

  return (
    <div className="bpm-scrim fixed inset-0 z-[1000] flex items-center justify-center px-5 py-4">
      <div
        className="bpm-card max-h-full w-full max-w-[420px] overflow-y-auto rounded-[34px] bg-white"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header band: the game's colours, with bubbles drifting up behind
            the result icon. */}
        <div
          className="relative flex h-[188px] shrink-0 flex-col items-center justify-center overflow-hidden text-white"
          style={{ background: `linear-gradient(140deg, ${accent} 0%, ${accentDeep} 100%)` }}
        >
          {HEADER_BUBBLES.map((bubble, index) => (
            <span
              key={index}
              aria-hidden
              className="bpm-rise absolute rounded-full"
              style={{
                left: bubble.left,
                width: bubble.size,
                height: bubble.size,
                background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85), rgba(255,255,255,0.15))",
                animationDelay: `${bubble.delay}s`,
                animationDuration: `${bubble.duration}s`,
              }}
            />
          ))}

          <span className="bpm-icon-pop relative flex h-[86px] w-[86px] items-center justify-center rounded-full bg-white/20"
            style={{ border: "2.5px solid rgba(255,255,255,0.65)", backdropFilter: "blur(2px)" }}
          >
            <span className="bpm-icon-bob text-white">
              <ResultIcon reason={reason} />
            </span>
          </span>
          <h2 className="bpm-title-in mt-3 px-6 text-center text-[28px] leading-tight" style={{ ...F, fontWeight: 700 }}>
            {title}
          </h2>
        </div>

        <div className="px-6 pb-6 pt-5 sm:px-7">
          {/* The score rolls up with a glowing pill behind it. */}
          <div className="bpm-score flex flex-col items-center">
            <span
              className="rounded-full px-7 py-1.5 text-[42px] leading-tight tabular-nums"
              style={{
                ...F,
                fontWeight: 700,
                color: accentDeep,
                background: `linear-gradient(180deg, ${accent}1F, ${accent}0A)`,
              }}
            >
              {displayScore}
            </span>
            <span className="mt-0.5 text-[15px] uppercase tracking-[0.18em]" style={{ ...F, color: "#7C8BAA", fontWeight: 650 }}>
              points
            </span>
          </div>

          {stats.length > 0 && (
            <div className="mt-3.5 space-y-2">
              {stats.map((stat, index) => (
                <StatChip key={stat.text} icon={stat.icon} tint={stat.tint} index={index}>
                  {stat.text}
                </StatChip>
              ))}
            </div>
          )}

          {favoritePop && (
            <div className="bpm-stat mt-2 flex items-center gap-3 rounded-[16px] bg-[#F4F8FF] px-3.5 py-2.5" style={{ animationDelay: "800ms" }}>
              <img
                src={favoritePop.asset.imageUrl}
                alt=""
                className="h-[34px] w-[34px] rounded-full border border-[#007AEF]/20 object-cover"
              />
              <span className="text-[15px]" style={{ ...F, color: "#22335C", fontWeight: 650 }}>
                You popped {favoritePop.asset.label} {favoritePop.count} times!
              </span>
            </div>
          )}

          {challengeTarget != null && (
            <div
              className="bpm-stat mt-3.5 rounded-[18px] px-4 py-3 text-center text-[17px]"
              style={{
                ...F,
                fontWeight: 700,
                animationDelay: "500ms",
                color: score > challengeTarget ? "#1D7A44" : "#22335C",
                background: score > challengeTarget ? "#E5F8EC" : "#F4F8FF",
                border: score > challengeTarget ? "1.5px solid #9BE0B6" : "1.5px solid #E1E9F8",
              }}
            >
              {score > challengeTarget
                ? `You beat the challenge! ${score} vs ${challengeTarget}.`
                : `Good game! The score to beat is ${challengeTarget}.`}
            </div>
          )}

          {starsAwarded > 0 && (
            <div
              className="bpm-stars mx-auto mt-4 flex w-fit items-center gap-2 rounded-full px-6 py-2.5 text-[17px]"
              style={{
                ...F,
                color: "#B07A00",
                fontWeight: 700,
                background: "linear-gradient(180deg, #FFF3CC, #FFE9A6)",
                border: "1.5px solid #FFDD80",
                boxShadow: "0 4px 12px rgba(255,199,41,0.35)",
              }}
            >
              <span className="bpm-star-spin text-[21px]" style={{ color: "#F2A900" }}>★</span>
              +{starsAwarded} stars earned
            </div>
          )}

          {isGuest && onSignup && (
            <button
              type="button"
              onClick={onSignup}
              className="bpm-stat mt-4 h-[54px] w-full rounded-full text-[17px] transition active:scale-[0.97]"
              style={{
                ...F,
                fontWeight: 700,
                animationDelay: "600ms",
                color: "#B07A00",
                background: "linear-gradient(180deg, #FFF3CC, #FFE9A6)",
                border: "1.5px solid #FFDD80",
                boxShadow: "0 4px 12px rgba(255,199,41,0.35)",
              }}
            >
              ★ Sign up to save your score and start creating
            </button>
          )}

          <button
            type="button"
            onClick={onRetry}
            className="bpm-cta mt-5 h-[60px] w-full rounded-full text-[20px] text-white transition active:scale-[0.97]"
            style={{
              ...F,
              fontWeight: 700,
              background: `linear-gradient(135deg, ${accent} 0%, ${accentDeep} 100%)`,
              boxShadow: `0 5px 0 color-mix(in srgb, ${accentDeep} 78%, black), 0 12px 24px ${accentDeep}4D`,
            }}
          >
            ↻&nbsp;&nbsp;Play Again
          </button>
          {onChallengeShare && (
            <button
              type="button"
              onClick={onChallengeShare}
              className="mt-2 h-12 w-full rounded-full text-[17px] transition hover:opacity-90 active:scale-[0.97]"
              style={{
                ...F,
                fontWeight: 700,
                color: accentDeep,
                background: `linear-gradient(180deg, ${accent}1F, ${accent}0A)`,
              }}
            >
              {challengeCopied ? "Link copied" : "Challenge a friend"}
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            className="mt-2 h-12 w-full text-[17px] transition hover:opacity-80"
            style={{ ...F, color: "#7C8BAA", fontWeight: 600 }}
          >
            All done
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bpmScrimIn { from { opacity: 0; } to { opacity: 1; } }
        .bpm-scrim {
          background: rgba(13, 36, 68, 0.55);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          animation: bpmScrimIn 300ms ease-out;
        }
        @keyframes bpmCardIn {
          0% { transform: translateY(34px) scale(0.86) rotate(-1.5deg); opacity: 0; }
          60% { transform: translateY(-6px) scale(1.03) rotate(0.5deg); opacity: 1; }
          100% { transform: translateY(0) scale(1) rotate(0); opacity: 1; }
        }
        .bpm-card {
          box-shadow: 0 30px 70px rgba(6, 30, 64, 0.4);
          animation: bpmCardIn 520ms cubic-bezier(0.2, 0.9, 0.3, 1.1) both;
        }
        @keyframes bpmRise {
          0% { bottom: -34px; opacity: 0; transform: translateX(0); }
          20% { opacity: 0.9; }
          100% { bottom: 105%; opacity: 0; transform: translateX(10px); }
        }
        .bpm-rise { bottom: -34px; animation: bpmRise linear infinite; }
        @keyframes bpmIconPop {
          0% { transform: scale(0); }
          65% { transform: scale(1.18); }
          100% { transform: scale(1); }
        }
        .bpm-icon-pop { animation: bpmIconPop 600ms cubic-bezier(0.2, 0.9, 0.3, 1.2) 180ms both; }
        @keyframes bpmIconBob {
          from { transform: translateY(-3px) rotate(-3deg); }
          to { transform: translateY(3px) rotate(3deg); }
        }
        .bpm-icon-bob { display: inline-flex; animation: bpmIconBob 1.9s ease-in-out infinite alternate; }
        @keyframes bpmTitleIn {
          from { transform: translateY(12px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .bpm-title-in { animation: bpmTitleIn 420ms ease-out 320ms both; }
        @keyframes bpmScoreIn {
          0% { transform: scale(0.6); opacity: 0; }
          70% { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .bpm-score { animation: bpmScoreIn 480ms cubic-bezier(0.2, 0.9, 0.3, 1.15) 300ms both; }
        @keyframes bpmStatIn {
          from { transform: translateY(10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .bpm-stat { animation: bpmStatIn 380ms ease-out both; }
        @keyframes bpmStarsIn {
          0% { transform: scale(0.5); opacity: 0; }
          70% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .bpm-stars { animation: bpmStarsIn 460ms cubic-bezier(0.2, 0.9, 0.3, 1.2) 850ms both; }
        @keyframes bpmStarSpin {
          0%, 70%, 100% { transform: rotate(0) scale(1); }
          80% { transform: rotate(18deg) scale(1.25); }
          90% { transform: rotate(-12deg) scale(1.1); }
        }
        .bpm-star-spin { display: inline-block; animation: bpmStarSpin 2.4s ease-in-out 1.4s infinite; }
        @keyframes bpmCtaIn {
          from { transform: translateY(14px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes bpmCtaBreath {
          from { transform: scale(1); }
          to { transform: scale(1.025); }
        }
        .bpm-cta {
          animation: bpmCtaIn 420ms ease-out 550ms both, bpmCtaBreath 1.1s ease-in-out 1.2s infinite alternate;
        }
        @media (prefers-reduced-motion: reduce) {
          .bpm-card, .bpm-rise, .bpm-icon-pop, .bpm-icon-bob, .bpm-title-in, .bpm-score,
          .bpm-stat, .bpm-stars, .bpm-star-spin, .bpm-cta { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default BubbleGameOverModal;
